/**
 * ConnectionService - Connection rules and compatibility logic
 * 
 * Handles signal type matching, connector compatibility, connection rules,
 * and group resolution utilities.
 */

import { CONNECTOR_TYPES, CONNECTOR_COMPATIBILITY } from '../constants/index.js';

// ============= GROUP RESOLUTION UTILITIES =============

/**
 * Get group from template by index (or legacy ID for backwards compat)
 * 
 * @param {Object} template - Device template with portGroups array
 * @param {number|string} groupRef - Group index (number) or legacy UUID string
 * @returns {Object|undefined} The port group object or undefined
 */
export function getGroupByRef(template, groupRef) {
    if (!template?.portGroups) return undefined;
    if (typeof groupRef === 'number') {
        return template.portGroups[groupRef];
    }
    if (typeof groupRef === 'string' && /^\d+$/.test(groupRef)) {
        return template.portGroups[parseInt(groupRef, 10)];
    }
    // Legacy UUID lookup
    return template.portGroups.find(g => g.id === groupRef);
}

/**
 * Get group index from reference (number or legacy UUID)
 * 
 * @param {Object} template - Device template with portGroups array
 * @param {number|string} groupRef - Group index (number) or legacy UUID string
 * @returns {number} The group index (0 if not found)
 */
export function getGroupIndex(template, groupRef) {
    if (typeof groupRef === 'number') {
        return groupRef;
    }
    if (typeof groupRef === 'string' && /^\d+$/.test(groupRef)) {
        return parseInt(groupRef, 10);
    }
    // Legacy UUID lookup
    if (!template?.portGroups) return 0;
    const idx = template.portGroups.findIndex(g => g.id === groupRef);
    return idx >= 0 ? idx : 0;
}

/**
 * Get group from a device instance by reference
 * 
 * @param {Object} device - Device instance
 * @param {number|string} groupRef - Group index or legacy UUID
 * @param {Function} getTemplateForDevice - Function to get template from device
 * @returns {Object|null} The port group object or null
 */
export function getGroupFromDevice(device, groupRef, getTemplateForDevice) {
    const template = getTemplateForDevice(device);
    if (!template) return null;
    return getGroupByRef(template, groupRef);
}

/**
 * Resolve a group reference to a numeric index
 * 
 * @param {string} deviceId - Device instance ID
 * @param {number|string} groupIdOrIndex - Group index, numeric string, or legacy UUID
 * @param {string} direction - Port direction ('input' or 'output') - currently unused but kept for API compat
 * @param {Object} instanceModel - Device instance model with getById method
 * @param {Function} getTemplateForDevice - Function to get template from device
 * @returns {number} The resolved group index (0 as fallback)
 */
export function resolveGroupIndex(deviceId, groupIdOrIndex, direction, instanceModel, getTemplateForDevice) {
    // If already a number, it's an index
    if (typeof groupIdOrIndex === 'number') {
        return groupIdOrIndex;
    }
    // If numeric string, parse it
    if (typeof groupIdOrIndex === 'string' && /^\d+$/.test(groupIdOrIndex)) {
        return parseInt(groupIdOrIndex, 10);
    }
    // Legacy UUID - find the index
    const device = instanceModel.getById(deviceId);
    if (!device) return 0;
    const template = getTemplateForDevice(device);
    if (!template) return 0;
    const idx = template.portGroups.findIndex(g => g.id === groupIdOrIndex);
    return idx >= 0 ? idx : 0;
}

// ============= CONNECTOR COMPATIBILITY =============

/**
 * Check if two connector types can physically connect
 * @param {string} sourceConnector - Source connector type (e.g., 'trs', 'xlr')
 * @param {string} targetConnector - Target connector type
 * @returns {boolean} True if connectors are physically compatible
 */
export function canConnectConnectors(sourceConnector, targetConnector) {
    // If either has no connector specified, allow (legacy data)
    if (!sourceConnector || !targetConnector) return true;
    // Same type always compatible
    if (sourceConnector === targetConnector) return true;
    // Check compatibility matrix
    const sourceCompat = CONNECTOR_COMPATIBILITY[sourceConnector] || [];
    return sourceCompat.includes(targetConnector);
}

/**
 * Get connector display icon HTML
 * @param {string} connectorType - Connector type key
 * @returns {string} HTML string with icon and tooltip, or empty string if not found
 */
export function getConnectorIcon(connectorType) {
    const info = CONNECTOR_TYPES[connectorType];
    if (!info) return '';
    return `<span class="connector-icon" title="${info.label}">${info.icon}</span>`;
}

/**
 * Get short code for connector (used in hover badges)
 * @param {string} connectorType - Connector type key
 * @returns {string} Short code (e.g., 'TRS', 'XLR') or the type itself as fallback
 */
export function getConnectorShortCode(connectorType) {
    const info = CONNECTOR_TYPES[connectorType];
    return info?.shortCode || connectorType || '';
}

/**
 * Get default connector type based on signal type
 * @param {string|string[]} signalType - Signal type or array of types
 * @returns {string} Default connector type for the signal
 */
export function getDefaultConnector(signalType) {
    const type = Array.isArray(signalType) ? signalType[0] : signalType;
    switch (type) {
        case 'audio':
            return 'trs';
        case 'midi':
        case 'midi-rtp':
            return 'midi-din';
        case 'midi-usb-host':
        case 'midi-usb-device':
            return 'usb-b';
        case 'cv':
        case 'gate':
            return '3.5mm';
        default:
            return 'trs';
    }
}

/**
 * Normalize signal type(s) to array format
 * @param {string|string[]|undefined} type - Signal type, array of types, or undefined
 * @returns {string[]} Array of signal types (defaults to ['audio'])
 */
export function normalizeSignalTypes(type) {
    if (!type) return ['audio'];
    if (Array.isArray(type)) return type;
    return [type];
}

/**
 * Check if a single source type matches a single target type
 * Handles special USB MIDI rules (host <-> device only)
 * @param {string} sourceType - Source signal type
 * @param {string} targetType - Target signal type
 * @returns {boolean} True if types are compatible
 */
export function checkSingleTypeMatch(sourceType, targetType) {
    // USB MIDI special rule: host <-> device only
    if (sourceType === 'midi-usb-host' && targetType === 'midi-usb-device') return true;
    if (sourceType === 'midi-usb-device' && targetType === 'midi-usb-host') return true;
    // Block USB host-to-host and device-to-device
    if (sourceType === 'midi-usb-host' || sourceType === 'midi-usb-device') return false;
    // All other types: same-to-same only
    return sourceType === targetType;
}

/**
 * Check if any source type matches any target type
 * @param {string|string[]} sourceTypes - Source signal type(s)
 * @param {string|string[]} targetTypes - Target signal type(s)
 * @returns {boolean} True if at least one source type matches one target type
 */
export function hasMatchingSignalType(sourceTypes, targetTypes) {
    const srcArr = normalizeSignalTypes(sourceTypes);
    const tgtArr = normalizeSignalTypes(targetTypes);
    
    for (const src of srcArr) {
        for (const tgt of tgtArr) {
            if (checkSingleTypeMatch(src, tgt)) return true;
        }
    }
    return false;
}

// MIDI type variants that can be internally routed to each other
const MIDI_TYPES = ['midi', 'midi-rtp', 'midi-usb-host', 'midi-usb-device'];

/**
 * Check if two types can be internally routed within a router device
 * More permissive than external connections:
 * - Any MIDI ↔ any MIDI (device handles conversion internally)
 * - Any audio ↔ any audio
 * @param {string|string[]} sourceType - Source signal type(s)
 * @param {string|string[]} targetType - Target signal type(s)
 * @returns {boolean} True if types can be internally routed
 */
export function canInternallyRoute(sourceType, targetType) {
    const srcArr = normalizeSignalTypes(sourceType);
    const tgtArr = normalizeSignalTypes(targetType);
    
    for (const src of srcArr) {
        for (const tgt of tgtArr) {
            // Any MIDI variant to any MIDI variant
            if (MIDI_TYPES.includes(src) && MIDI_TYPES.includes(tgt)) return true;
            // Same type (audio, cv, etc.)
            if (src === tgt) return true;
        }
    }
    return false;
}

/**
 * Check if two ports can connect based on signal type and direction
 * 
 * @param {string|string[]} sourceType - Source signal type(s)
 * @param {string|string[]} targetType - Target signal type(s)
 * @param {string} sourceDirection - 'output' or 'insert'
 * @param {string} targetDirection - 'input' or 'insert'
 * @param {Object} options - { sourceConnector, targetConnector }
 */
export function canConnectTypes(sourceType, targetType, sourceDirection = 'output', targetDirection = 'input', options = {}) {
    // Direction validation:
    // Source must be output or insert (can send signal)
    if (sourceDirection !== 'output' && sourceDirection !== 'insert') return false;
    // Target must be input or insert (can receive signal)
    if (targetDirection !== 'input' && targetDirection !== 'insert') return false;
    
    // Check connector physical compatibility (if connectors specified)
    if (options.sourceConnector && options.targetConnector) {
        if (!canConnectConnectors(options.sourceConnector, options.targetConnector)) {
            return false;
        }
    }
    
    // Check signal type compatibility
    return hasMatchingSignalType(sourceType, targetType);
}

// Default port connection rules
const DEFAULT_PORT_CONNECTION_RULES = {
    'midi':           { fanOut: true,  fanIn: true  },
    'midi-rtp':       { fanOut: true,  fanIn: true  },
    'midi-usb-host':  { fanOut: true,  fanIn: false },
    'midi-usb-device':{ fanOut: true,  fanIn: false },
    'audio':          { fanOut: false, fanIn: false },
    'cv':             { fanOut: true,  fanIn: false },
    'gate':           { fanOut: true,  fanIn: false },
    'clock':          { fanOut: true,  fanIn: false },
    'insert':         { fanOut: false, fanIn: false },
    '_default':       { fanOut: false, fanIn: false }
};

/**
 * Get connection rules for a port type (fan-in/fan-out behavior)
 * @param {string} portType - Signal type (e.g., 'audio', 'midi', 'cv')
 * @param {Object} [customRules={}] - Custom rules to override defaults
 * @returns {{fanOut: boolean, fanIn: boolean}} Connection rules object
 */
export function getConnectionRules(portType, customRules = {}) {
    const rules = { ...DEFAULT_PORT_CONNECTION_RULES, ...customRules };
    return rules[portType] || rules['_default'];
}
